import React, { useState, useEffect } from "react";
import SpreetailHeader from "../layout/SpreetailHeader";
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { ColumnSemantics, TableColumnSemantics } from "../../../shared/rcaApiService";

export type { ColumnSemantics, TableColumnSemantics };

interface ColumnSemanticsViewProps {
    tables: TableColumnSemantics[];
    onSemanticsChange: (tables: TableColumnSemantics[]) => void;
    onGeneratePlan: () => void;
    isGeneratingPlan: boolean;
    userQuestion: string;
    onSelectedColumnsChange?: (selectedKeys: Set<string>) => void;
}

const ColumnSemanticsView: React.FC<ColumnSemanticsViewProps> = ({
    tables,
    onSemanticsChange,
    onGeneratePlan,
    isGeneratingPlan,
    userQuestion,
    onSelectedColumnsChange
}) => {
    const [editingKey, setEditingKey] = useState<string | null>(null); // "tableIndex-columnIndex"
    const [originalValue, setOriginalValue] = useState<string>("");
    const [expandedTables, setExpandedTables] = useState<Set<number>>(new Set());
    const [selectedColumns, setSelectedColumns] = useState<Set<string>>(new Set());
    const editableRefs = React.useRef<Record<string, HTMLDivElement | null>>({});

    // Initialize all tables as expanded on first render
    useEffect(() => {
        if (tables.length > 0 && expandedTables.size === 0) {
            setExpandedTables(new Set(tables.map((_, index) => index)));
        }
    }, [tables.length]);

    // Initialize all columns as selected by default
    useEffect(() => {
        if (tables.length > 0) {
            const allColumnKeys = new Set<string>();
            tables.forEach((table, tableIndex) => {
                table.columns.forEach((_, columnIndex) => {
                    allColumnKeys.add(`${tableIndex}-${columnIndex}`);
                });
            });
            setSelectedColumns(allColumnKeys);
            onSelectedColumnsChange?.(allColumnKeys);
        }
    }, [tables]);


    const toggleTableExpanded = (tableIndex: number) => {
        setExpandedTables(prev => {
            const newSet = new Set(prev);
            if (newSet.has(tableIndex)) {
                newSet.delete(tableIndex);
            } else {
                newSet.add(tableIndex);
            }
            return newSet;
        });
    };

    const handleStartEdit = (tableIndex: number, columnIndex: number) => {
        const key = `${tableIndex}-${columnIndex}`;
        const currentMeaning = tables[tableIndex]?.columns[columnIndex]?.meaning || '';
        setOriginalValue(currentMeaning);
        setEditingKey(key);
        
        // Focus the editable element after state update
        setTimeout(() => {
            const editableEl = editableRefs.current[key];
            if (editableEl) {
                editableEl.focus();
                // Move cursor to end
                const range = document.createRange();
                const sel = window.getSelection();
                range.selectNodeContents(editableEl);
                range.collapse(false);
                sel?.removeAllRanges();
                sel?.addRange(range);
            }
        }, 0);
    };

    const handleSaveEdit = () => {
        if (editingKey === null) return;

        const editableEl = editableRefs.current[editingKey];
        if (!editableEl) return;

        const newValue = editableEl.innerText.trim();
        
        // Check if value actually changed
        if (newValue === originalValue) {
            setEditingKey(null);
            setOriginalValue("");
            return;
        }

        const [tableIndex, columnIndex] = editingKey.split('-').map(Number);
        const updatedTables = [...tables];
        const updatedColumns = [...updatedTables[tableIndex].columns];
        updatedColumns[columnIndex] = {
            ...updatedColumns[columnIndex],
            meaning: newValue || ''
        };
        updatedTables[tableIndex] = {
            ...updatedTables[tableIndex],
            columns: updatedColumns
        };
        onSemanticsChange(updatedTables);
        setEditingKey(null);
        setOriginalValue("");
    };

    const handleCancelEdit = () => {
        // Restore original value
        if (editingKey) {
            const editableEl = editableRefs.current[editingKey];
            if (editableEl) {
                editableEl.innerText = originalValue;
            }
        }
        setEditingKey(null);
        setOriginalValue("");
    };

    const getTotalColumnCount = () => {
        return tables.reduce((sum, table) => sum + table.columns.length, 0);
    };

    const handleColumnSelectionChange = (tableIndex: number, columnIndex: number) => {
        const key = `${tableIndex}-${columnIndex}`;
        setSelectedColumns(prev => {
            const newSet = new Set(prev);
            if (newSet.has(key)) {
                newSet.delete(key);
            } else {
                newSet.add(key);
            }
            onSelectedColumnsChange?.(newSet);
            return newSet;
        });
    };

    return (
        <>
            <style>
                {`
                @keyframes spin {
                    from { transform: rotate(0deg); }
                    to { transform: rotate(360deg); }
                }
                .bg-gradient-custom {
                    background: linear-gradient(180deg, #D3E8D9 0%, #EEEEEE 79.45%);
                }
                .hide-scrollbar::-webkit-scrollbar {
                    display: none;
                }
                .hide-scrollbar {
                    -ms-overflow-style: none;
                    scrollbar-width: none;
                }
                
                /* Your Question Card - matches the image exactly */
                .question-card {
                    background: linear-gradient(135deg, #D5EAD8 0%, #C8E0CB 100%);
                    border-radius: 12px;
                    padding: 10px 14px;
                    margin-bottom: 16px;
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    gap: 10px;
                }
                .question-card-content {
                    flex: 1;
                }
                .question-card-label {
                    font-size: 11px;
                    font-weight: 600;
                    color: #068E75;
                    margin: 0 0 4px 0;
                }
                .question-card-text {
                    font-size: 12px;
                    font-weight: 500;
                    color: #1F2937;
                    line-height: 1.4;
                    margin: 0;
                }
                .question-card-icon {
                    width: 60px;
                    height: 62px;
                    flex-shrink: 0;
                }
                
                /* Parent Transparent Card Container for Tables */
                .tables-parent-card {
                    background: rgba(255, 255, 255, 0.5);
                    border-radius: 16px;
                    padding: 16px;
                    margin-bottom: 16px;
                }
                
                /* Tables Section Header */
                .tables-section-header {
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    margin-bottom: 12px;
                    padding-left: 0;
                }
                .tables-icon {
                    width: 20px;
                    height: 20px;
                }
                .tables-title {
                    font-size: 14px;
                    font-weight: 600;
                    color: #1F2937;
                    margin: 0;
                }
                
                /* Sheet Accordion - matches image exactly */
                .sheet-accordion {
                    background: rgba(255, 255, 255, 0.85);
                    border-radius: 14px;
                    margin-bottom: 8px;
                    overflow: hidden;
                    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
                    border: 1px solid rgba(0, 0, 0, 0.04);
                }
                .sheet-accordion:last-child {
                    margin-bottom: 0;
                }
                .sheet-accordion-header {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    padding: 12px 16px;
                    cursor: pointer;
                    transition: background 0.2s ease;
                }
                .sheet-accordion-header:hover {
                    background: rgba(255, 255, 255, 0.5);
                }
                .sheet-name {
                    font-size: 13px;
                    font-weight: 600;
                    color: #1F2937;
                    margin: 0;
                }
                .sheet-accordion-arrow-container {
                    width: 32px;
                    height: 32px;
                    background: white;
                    border-radius: 10px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    flex-shrink: 0;
                }
                .sheet-accordion-arrow {
                    width: 16px;
                    height: 16px;
                    transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
                }
                
                /* Accordion Content */
                .sheet-accordion-content {
                    max-height: 0;
                    overflow: hidden;
                    opacity: 0;
                    transition: max-height 0.3s cubic-bezier(0.4, 0, 0.2, 1), 
                                padding 0.3s cubic-bezier(0.4, 0, 0.2, 1),
                                opacity 0.25s ease;
                }
                .sheet-accordion.expanded .sheet-accordion-content {
                    max-height: 2000px;
                    padding: 0 12px 12px 12px;
                    opacity: 1;
                }
                
                /* Column Card - matches image exactly */
                .column-card {
                    background: rgba(255, 255, 255, 0.85);
                    border-radius: 10px;
                    padding: 12px 14px;
                    margin-bottom: 8px;
                    border: 1px solid #E5E7EB;
                    transition: all 0.2s ease;
                }
                .column-card:last-child {
                    margin-bottom: 0;
                }
                .column-card:hover {
                    border-color: #D1D5DB;
                }
                .column-card.editing {
                    border-color: #068E75;
                }
                .column-card.unselected {
                    opacity: 0.5;
                    background: rgba(255, 255, 255, 0.6);
                }
                
                /* Column Header */
                .column-header {
                    display: flex;
                    align-items: flex-start;
                    justify-content: space-between;
                    gap: 12px;
                }
                .column-header-left {
                    display: flex;
                    align-items: flex-start;
                    gap: 12px;
                    flex: 1;
                    min-width: 0;
                }
                
                /* Custom Checkbox - matches image (rounded corners, blue when checked) */
                .column-checkbox {
                    width: 18px;
                    height: 18px;
                    border-radius: 5px;
                    border: 2px solid #3B82F6;
                    background: white;
                    cursor: pointer;
                    appearance: none;
                    -webkit-appearance: none;
                    flex-shrink: 0;
                    transition: all 0.2s ease;
                    position: relative;
                    margin-top: 1px;
                }
                .column-checkbox:checked {
                    background: #3B82F6;
                    border-color: #3B82F6;
                }
                .column-checkbox:checked::after {
                    content: '';
                    position: absolute;
                    left: 5px;
                    top: 1px;
                    width: 4px;
                    height: 9px;
                    border: solid white;
                    border-width: 0 2px 2px 0;
                    transform: rotate(45deg);
                }
                .column-checkbox:hover {
                    border-color: #2563EB;
                }
                
                /* Column Info */
                .column-info {
                    flex: 1;
                    min-width: 0;
                }
                .column-name {
                    font-size: 12px;
                    font-weight: 600;
                    color: #1F2937;
                    margin: 0 0 4px 0;
                }
                .column-meaning {
                    font-size: 12px;
                    color: #6B7280;
                    line-height: 1.5;
                    margin: 0;
                    display: -webkit-box;
                    -webkit-line-clamp: 3;
                    -webkit-box-orient: vertical;
                    overflow: hidden;
                }
                
                /* Edit Button - pencil icon (no background) */
                .edit-btn {
                    background: none;
                    border: none;
                    padding: 0;
                    cursor: pointer;
                    opacity: 0.7;
                    transition: all 0.2s ease;
                    flex-shrink: 0;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                }
                .edit-btn:hover {
                    opacity: 1;
                    background: transparent;
                }
                
                /* Editable Field */
                .meaning-editable {
                    font-size: 12px;
                    color: #374151;
                    line-height: 1.5;
                    margin: 6px 0 0 0;
                    padding: 8px 10px;
                    border: 1.5px solid #068E75;
                    border-radius: 6px;
                    background: white;
                    min-height: 18px;
                    outline: none;
                    transition: all 0.2s ease;
                    box-shadow: 0 0 0 3px rgba(6, 142, 117, 0.1);
                    white-space: pre-wrap;
                    word-wrap: break-word;
                    overflow-wrap: break-word;
                    cursor: text;
                }
                .meaning-editable:focus {
                    box-shadow: 0 0 0 3px rgba(6, 142, 117, 0.15);
                }
                .meaning-editable:empty:before {
                    content: attr(data-placeholder);
                    color: #9CA3AF;
                    font-style: italic;
                }
                
                /* Edit Actions */
                .edit-actions {
                    display: flex;
                    gap: 6px;
                    margin-top: 10px;
                    justify-content: flex-end;
                }
                .edit-action-btn {
                    padding: 6px 12px;
                    font-size: 11px;
                    font-weight: 500;
                    border-radius: 6px;
                    cursor: pointer;
                    transition: all 0.2s ease;
                }
                .edit-action-btn.save {
                    background: #068E75;
                    color: white;
                    border: none;
                }
                .edit-action-btn.save:hover {
                    background: #057A63;
                }
                .edit-action-btn.cancel {
                    background: white;
                    color: #6B7280;
                    border: 1px solid #E5E7EB;
                }
                .edit-action-btn.cancel:hover {
                    background: #F9FAFB;
                    border-color: #D1D5DB;
                }
                
                /* Columns scroll container */
                .columns-scroll-container {
                    max-height: 400px;
                    overflow-y: auto;
                    -ms-overflow-style: none;
                    scrollbar-width: none;
                }
                .columns-scroll-container::-webkit-scrollbar {
                    display: none;
                }
                `}
            </style>
            <div className="h-screen flex flex-col relative font-sans text-gray-800 bg-gradient-custom">
                <div className="absolute -right-10 -top-10 w-50 h-50 blur-[40px] pointer-events-none"
                    style={{ background: 'radial-gradient(circle at center, rgba(134, 239, 172, 0.3), transparent 70%)' }}></div>
                <div className="absolute -left-8 -bottom-8 w-38 h-38 blur-[40px] pointer-events-none"
                    style={{ background: 'radial-gradient(circle at center, rgba(167, 243, 208, 0.3), transparent 70%)' }}></div>

                <SpreetailHeader />

                <div className="flex-1 overflow-y-auto px-5 pb-5 min-h-0 hide-scrollbar">
                    <div className="max-w-md mx-auto pt-3">
                        {/* Header */}
                        <div className="text-left mb-5">
                            <h1 className="text-lg font-medium leading-none text-gray-800 m-0 mb-3">
                                Column Definitions
                            </h1>
                            <p className="text-[13px] text-gray-600 mb-4">
                                Review and edit the semantic meaning of each column to improve analysis accuracy
                            </p>
                        </div>

                        {/* Your Question Card - matches image exactly */}
                        <div className="question-card">
                            <div className="question-card-content">
                                <p className="question-card-label">Your Question</p>
                                <p className="question-card-text">
                                    {userQuestion.length > 100 ? userQuestion.substring(0, 100) + "..." : userQuestion}
                                </p>
                            </div>
                            <img 
                                src="assets/question-mark.png" 
                                alt="Question" 
                                className="question-card-icon"
                            />
                        </div>

                        {/* Tables Parent Transparent Card */}
                        <div className="tables-parent-card">
                            {/* Tables Section Header */}
                            <div className="tables-section-header">
                                <img 
                                    src="assets/table.png" 
                                    alt="Tables" 
                                    className="tables-icon"
                                />
                                <h2 className="tables-title">Tables</h2>
                            </div>

                            {/* Sheet Accordions */}
                            <div className="tables-accordions-container">
                                {tables.map((table, tableIndex) => (
                                    <div
                                        key={tableIndex}
                                        className={`sheet-accordion ${expandedTables.has(tableIndex) ? 'expanded' : ''}`}
                                    >
                                        {/* Accordion Header */}
                                        <div
                                            className="sheet-accordion-header"
                                            onClick={() => toggleTableExpanded(tableIndex)}
                                        >
                                            <h3 className="sheet-name">{table.tableName}</h3>
                                            <div className="sheet-accordion-arrow-container">
                                                <svg 
                                                    width="16" 
                                                    height="16" 
                                                    viewBox="0 0 24 24" 
                                                    fill="none" 
                                                    xmlns="http://www.w3.org/2000/svg"
                                                    className="sheet-accordion-arrow"
                                                    style={{ transform: expandedTables.has(tableIndex) ? 'rotate(180deg)' : 'rotate(0deg)' }}
                                                >
                                                    <path 
                                                        d="M6 9L12 15L18 9" 
                                                        stroke="#068E75" 
                                                        strokeWidth="2" 
                                                        strokeLinecap="round" 
                                                        strokeLinejoin="round"
                                                    />
                                                </svg>
                                            </div>
                                        </div>

                                        {/* Accordion Content */}
                                        <div className="sheet-accordion-content">
                                            <div className="columns-scroll-container">
                                                {table.columns.map((col, columnIndex) => {
                                                    const editKey = `${tableIndex}-${columnIndex}`;
                                                    const isEditing = editingKey === editKey;
                                                    const isSelected = selectedColumns.has(editKey);

                                                    return (
                                                        <div
                                                            key={columnIndex}
                                                            className={`column-card ${isEditing ? 'editing' : ''} ${!isSelected ? 'unselected' : ''}`}
                                                        >
                                                            <div className="column-header">
                                                                <div className="column-header-left">
                                                                    <input
                                                                        type="checkbox"
                                                                        className="column-checkbox"
                                                                        checked={isSelected}
                                                                        onChange={() => handleColumnSelectionChange(tableIndex, columnIndex)}
                                                                        onClick={(e) => e.stopPropagation()}
                                                                    />
                                                                    <div className="column-info">
                                                                        <h4 className="column-name">{col.columnName}</h4>
                                                                        {!isEditing && (
                                                                            <p className="column-meaning">
                                                                                {col.meaning || "No description provided"}
                                                                            </p>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                                {!isEditing && (
                                                                <button
                                                                    className="edit-btn"
                                                                    onClick={() => handleStartEdit(tableIndex, columnIndex)}
                                                                    title="Edit meaning"
                                                                >
                                                                    {/* Pencil icon matching execution steps UI */}
                                                                    <svg width="18" height="18" viewBox="0 0 24 26" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                                        <path d="M4 20H8L18.5 9.5C19.0304 8.96957 19.3284 8.25014 19.3284 7.5C19.3284 6.74986 19.0304 6.03043 18.5 5.5C17.9696 4.96957 17.2501 4.67157 16.5 4.67157C15.7499 4.67157 15.0304 4.96957 14.5 5.5L4 16V20Z" stroke="#068E75" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                                                        <path d="M13.5 6.5L17.5 10.5" stroke="#068E75" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                                                        <path d="M4 24H20" stroke="#068E75" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                                                    </svg>
                                                                </button>
                                                                )}
                                                            </div>

                                                            {isEditing && (
                                                                <>
                                                                    <div
                                                                        ref={(el) => { editableRefs.current[editKey] = el; }}
                                                                        className="meaning-editable"
                                                                        contentEditable
                                                                        suppressContentEditableWarning
                                                                        data-placeholder="Enter column meaning..."
                                                                        onKeyDown={(e) => e.stopPropagation()}
                                                                        onKeyUp={(e) => e.stopPropagation()}
                                                                        onMouseDown={(e) => e.stopPropagation()}
                                                                        onClick={(e) => e.stopPropagation()}
                                                                    >
                                                                        {col.meaning}
                                                                    </div>
                                                                    <div className="edit-actions">
                                                                        <button
                                                                            className="edit-action-btn cancel"
                                                                            onClick={handleCancelEdit}
                                                                        >
                                                                            Cancel
                                                                        </button>
                                                                        <button
                                                                            className="edit-action-btn save"
                                                                            onClick={handleSaveEdit}
                                                                        >
                                                                            Save
                                                                        </button>
                                                                    </div>
                                                                </>
                                                            )}
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>

                    </div>
                </div>

                {/* Footer Section with Generate Plan Button */}
                <div className="w-full px-5 py-3" style={{ backgroundColor: 'rgba(211, 232, 217, 0.5)' }}>
                    <div className="w-full max-w-md mx-auto">
                        <button
                            onClick={onGeneratePlan}
                            disabled={isGeneratingPlan || getTotalColumnCount() === 0}
                            className={`w-full text-white border-none rounded-lg py-3 px-4 text-[14px] font-medium transition-all duration-200 flex items-center justify-center gap-2 ${
                                isGeneratingPlan
                                    ? 'bg-gray-400 cursor-not-allowed opacity-60'
                                    : 'bg-[#068E75] cursor-pointer hover:bg-[#057A63] hover:-translate-y-px'
                            }`}
                            style={{
                                boxShadow: isGeneratingPlan
                                    ? 'none'
                                    : '0 2px 8px rgba(6, 142, 117, 0.3)'
                            }}
                        >
                            {isGeneratingPlan ? (
                                <>
                                    <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full"
                                        style={{ animation: 'spin 1s linear infinite' }}></div>
                                    Generating Plan...
                                </>
                            ) : (
                                <>
                                    <AutoAwesomeIcon sx={{ fontSize: 14 }} />
                                    Generate Plan
                                </>
                            )}
                        </button>
                    </div>
                </div>
            </div>
        </>
    );
};

// Helper function to convert flat columns to table format
// Note: Descriptions should come from LLM via API, this just creates the structure
export const columnsToTableSemantics = (
    columns: string[],
    tableName: string = "Data Table"
): TableColumnSemantics => {
    return {
        tableName,
        columns: columns.map(col => ({
            columnName: col,
            meaning: "" // Descriptions populated by LLM API
        }))
    };
};

// Helper function to flatten table semantics back to column semantics array
export const tableSemanticsToColumns = (tables: TableColumnSemantics[]): ColumnSemantics[] => {
    return tables.flatMap(table => table.columns);
};

export default ColumnSemanticsView;
